import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { Search, Sparkles, X } from "lucide-react";
import axios from "utils/axios";
import { useGlobalSearch } from "hooks/useGlobalSearch";
import { filterVisibleMatches } from "utils/searchVisibleMatch";
import { VeilleV2Card, AvisV2Card } from "components/v2/EditorialCards";
import {
  FormationV2Card,
  MasterclassV2Card,
  ModuleV2Card,
  EventV2Card,
  LibraryV2Card,
  ExpertV2Card,
  InstitutionV2Card,
} from "components/v2/ListingCards";
import "pages/veille/veilleV2.css";
import "pages/formationV2.css";
import "pages/experts.css";
import "pages/institutions.css";
import "./globalSearch.css";

const PAGE_SIZE = 8;
const MIN_CHARS = 2;
const DEBOUNCE_MS = 400;
const EXIT_MS = 420;

const EMPTY_RESULTS = {
  experts: [],
  news: [],
  opinions: [],
  institutions: [],
  formations: [],
  masterclasses: [],
  events: [],
  modules: [],
  library: [],
};

const SEARCH_SOURCES = [
  {
    key: "news",
    type: "news",
    filterId: "news",
    label: "Notes",
    url: "/api/monitoring/news/",
    params: { page_size: PAGE_SIZE, lightweight: true },
  },
  {
    key: "opinions",
    type: "opinion",
    filterId: "opinion",
    label: "Avis d'expert",
    url: "/api/experts/opinions/",
    params: { page_size: PAGE_SIZE },
  },
  {
    key: "formations",
    type: "formation",
    filterId: "formation",
    label: "Formations",
    url: "/api/formations/",
    params: { page_size: PAGE_SIZE, lightweight: true },
  },
  {
    key: "masterclasses",
    type: "masterclass",
    filterId: "masterclass",
    label: "Masterclass",
    url: "/api/formations/masterclass/",
    params: { page_size: PAGE_SIZE, lightweight: true },
  },
  {
    key: "modules",
    type: "module",
    filterId: "module",
    label: "Modules",
    url: "/api/modules/",
    params: { page_size: PAGE_SIZE },
  },
  {
    key: "events",
    type: "event",
    filterId: "event",
    label: "Événements",
    url: "/api/monitoring/events/",
    params: { page_size: PAGE_SIZE },
  },
  {
    key: "library",
    type: "library",
    filterId: "library",
    label: "Bibliothèque",
    url: "/api/library/list/",
    params: { page_size: PAGE_SIZE },
  },
  {
    key: "experts",
    type: "expert",
    filterId: "expert",
    label: "Experts",
    url: "/api/experts/list/",
    params: { page_size: PAGE_SIZE },
  },
  {
    key: "institutions",
    type: "institution",
    filterId: "institution",
    label: "Institutions",
    url: "/api/institutions/list/",
    params: { page_size: PAGE_SIZE },
  },
];

const FILTERS = [
  { id: "all", label: "Tous" },
  ...SEARCH_SOURCES.map((s) => ({ id: s.filterId, label: s.label })),
];

function emptyCounts() {
  return FILTERS.reduce((acc, f) => {
    acc[f.id] = 0;
    return acc;
  }, {});
}

export default function GlobalSearch() {
  const { open, close } = useGlobalSearch();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  const [entered, setEntered] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [failedKeys, setFailedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("all");

  useEffect(() => {
    if (open) {
      setMounted(true);
      const id = requestAnimationFrame(() => {
        requestAnimationFrame(() => setEntered(true));
      });
      return () => cancelAnimationFrame(id);
    }
    setEntered(false);
    const t = setTimeout(() => setMounted(false), EXIT_MS);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!entered) return undefined;
    const t = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [entered]);

  const resetState = useCallback(() => {
    setSearchTerm("");
    setResults(EMPTY_RESULTS);
    setFailedKeys([]);
    setLoading(false);
    setSelectedFilter("all");
  }, []);

  useEffect(() => {
    if (!open) {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      const t = setTimeout(resetState, EXIT_MS);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open, resetState]);

  const debouncedSearch = useRef(
    debounce(async (term) => {
      const cleanedTerm = (term || "").trim();

      if (!cleanedTerm || cleanedTerm.length < MIN_CHARS) {
        setLoading(false);
        setResults(EMPTY_RESULTS);
        setFailedKeys([]);
        return;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      setLoading(true);

      try {
        const settled = await Promise.allSettled(
          SEARCH_SOURCES.map((source) =>
            axios.get(source.url, {
              params: { search: cleanedTerm, ...source.params },
              signal: abortController.signal,
            }),
          ),
        );

        if (abortControllerRef.current !== abortController) return;

        const next = { ...EMPTY_RESULTS };
        const failed = [];

        settled.forEach((res, i) => {
          const source = SEARCH_SOURCES[i];
          if (res.status !== "fulfilled") {
            failed.push(source.key);
            return;
          }
          const items = res.value.data?.results || res.value.data || [];
          const list = Array.isArray(items) ? items : [];
          // Drop hits that only matched hidden fields (description, pitch, …)
          next[source.key] = filterVisibleMatches(
            list,
            source.type,
            cleanedTerm,
          );
        });

        setResults(next);
        setFailedKeys(failed);
        setSelectedFilter("all");
        setLoading(false);
      } catch (error) {
        if (error.name === "AbortError" || error.code === "ERR_CANCELED") return;
        if (abortControllerRef.current === abortController) {
          setResults(EMPTY_RESULTS);
          setFailedKeys(SEARCH_SOURCES.map((s) => s.key));
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS),
  );

  useEffect(() => {
    const instance = debouncedSearch.current;
    if (mounted && entered) {
      instance(searchTerm);
    }
    return () => instance.cancel();
  }, [searchTerm, mounted, entered]);

  const filterCounts = useMemo(() => {
    const counts = emptyCounts();
    let total = 0;
    SEARCH_SOURCES.forEach((source) => {
      const n = results[source.key]?.length || 0;
      counts[source.filterId] = n;
      total += n;
    });
    counts.all = total;
    return counts;
  }, [results]);

  const flatResults = useMemo(() => {
    const items = [];
    SEARCH_SOURCES.forEach((source) => {
      (results[source.key] || []).forEach((item) => {
        items.push({ ...item, _searchType: source.type, _filterId: source.filterId });
      });
    });
    return items;
  }, [results]);

  const filteredItems = useMemo(() => {
    if (selectedFilter === "all") return flatResults;
    return flatResults.filter((item) => item._filterId === selectedFilter);
  }, [flatResults, selectedFilter]);

  const visibleFilters = useMemo(
    () =>
      FILTERS.filter(
        (filter) => filter.id === "all" || filterCounts[filter.id] > 0,
      ),
    [filterCounts],
  );

  const failedLabels = failedKeys
    .map((key) => SEARCH_SOURCES.find((s) => s.key === key)?.label)
    .filter(Boolean);

  const hasQuery = searchTerm.trim().length >= MIN_CHARS;
  const hasResults = flatResults.length > 0;

  const go = useCallback(
    (path) => {
      close();
      navigate(path);
    },
    [close, navigate],
  );

  const renderItem = (item, index) => {
    const type = item._searchType;
    const key = `${type}-${item.id}-${index}`;
    const highlightQuery = searchTerm.trim();

    if (type === "news") {
      return (
        <VeilleV2Card
          key={key}
          item={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/veille/${id}`)}
        />
      );
    }
    if (type === "opinion") {
      return (
        <AvisV2Card
          key={key}
          item={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/avis/${id}`)}
        />
      );
    }
    if (type === "formation") {
      return (
        <FormationV2Card
          key={key}
          item={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/formation/${id}`)}
        />
      );
    }
    if (type === "masterclass") {
      return (
        <MasterclassV2Card
          key={key}
          item={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/masterclass/${id}`)}
        />
      );
    }
    if (type === "module") {
      return (
        <ModuleV2Card
          key={key}
          item={item}
          highlightQuery={highlightQuery}
          onOpen={(mod) => {
            const fid = mod.formation_id || mod.formation?.id;
            const mid = mod.masterclass_id || mod.masterclass?.id;
            if (fid && mod.order != null) {
              go(`/formation/${fid}/modules/${mod.order}`);
            } else if (mid && mod.order != null) {
              go(`/masterclass/${mid}/modules/${mod.order}`);
            } else if (fid) {
              go(`/formation/${fid}`);
            } else {
              go("/modules");
            }
          }}
        />
      );
    }
    if (type === "event") {
      return (
        <EventV2Card
          key={key}
          item={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/event/${id}`)}
        />
      );
    }
    if (type === "library") {
      return (
        <LibraryV2Card
          key={key}
          item={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/library/${id}`)}
        />
      );
    }
    if (type === "expert") {
      return (
        <ExpertV2Card
          key={key}
          expert={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/experts/${id}`)}
        />
      );
    }
    if (type === "institution") {
      return (
        <InstitutionV2Card
          key={key}
          institution={item}
          highlightQuery={highlightQuery}
          onOpen={(id) => go(`/institutions/${id}`)}
        />
      );
    }
    return null;
  };

  if (!mounted) return null;

  return (
    <div
      className={`gs-page${entered ? " is-in" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Recherche"
    >
      <div className="gs-page-inner v2-root">
        <div className="v2-shell">
          <header className="gs-hero v2-hero">
            <div className="gs-title-row">
              <h1 className="gs-title">Recherche</h1>
              <button
                type="button"
                className="gs-close"
                aria-label="Fermer la recherche"
                onClick={close}
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            <label className="gs-bar">
              <Search className="gs-bar-icon" size={20} strokeWidth={1.7} />
              <input
                ref={inputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher formations, actualités, experts…"
                autoComplete="off"
                spellCheck={false}
                enterKeyHint="search"
              />
              {searchTerm ? (
                <button
                  type="button"
                  className="gs-bar-clear"
                  aria-label="Effacer"
                  onClick={() => {
                    setSearchTerm("");
                    inputRef.current?.focus();
                  }}
                >
                  <X size={16} />
                </button>
              ) : null}
            </label>

            {!hasQuery && (
              <p className="gs-hint">
                Tapez au moins {MIN_CHARS} caractères pour lancer la recherche.
              </p>
            )}

            {hasQuery && hasResults && visibleFilters.length > 1 && (
              <div className="v2-categories" style={{ marginTop: 4 }}>
                {visibleFilters.map((filter) => {
                  const count = filterCounts[filter.id] || 0;
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      className={`v2-cat${selectedFilter === filter.id ? " is-active" : ""}`}
                      onClick={() => setSelectedFilter(filter.id)}
                    >
                      {filter.label}
                      {count > 0 ? (
                        <span style={{ opacity: 0.7, marginLeft: 6 }}>
                          {count}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            )}

            {hasQuery && hasResults && (
              <div className="v2-meta-row">
                <p className="v2-count">
                  <Sparkles
                    size={14}
                    style={{ verticalAlign: "-2px", marginRight: 6 }}
                  />
                  <strong>{filteredItems.length}</strong>{" "}
                  {filteredItems.length === 1 ? "résultat" : "résultats"}
                </p>
              </div>
            )}
          </header>

          {hasQuery && (
            <div className="gs-results">
              {failedLabels.length > 0 && hasResults && (
                <div className="gs-partial-error">
                  Certaines sources n&apos;ont pas répondu :{" "}
                  {failedLabels.join(", ")}
                </div>
              )}

              {loading && !hasResults ? (
                <div className="v2-loading">
                  <div className="v2-spinner" />
                  <div>Recherche en cours…</div>
                </div>
              ) : !hasResults ? (
                <div className="v2-empty">
                  <Search size={28} strokeWidth={1.75} />
                  <div>
                    {failedLabels.length === SEARCH_SOURCES.length
                      ? "La recherche est temporairement indisponible."
                      : `Aucun résultat pour « ${searchTerm.trim()} »`}
                  </div>
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="v2-empty">
                  <div>Aucun élément dans cette catégorie.</div>
                  <button
                    type="button"
                    className="v2-page-btn"
                    onClick={() => setSelectedFilter("all")}
                  >
                    Voir tous les résultats
                  </button>
                </div>
              ) : (
                <div className="f2-grid">
                  {filteredItems.map((item, index) => renderItem(item, index))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
